import {i18n} from '../i18n/i18n'

import React, {Component} from 'react'
import {ConfirmParams} from '../components/Confirm/Confirm.state'
import confirmService from '../services/confirm.service'
import alertService from '../services/alert.service'
import itemService from '../services/item.service'
import itemApi, {ItemData} from '../apis/item.api'
import {ItemType} from '../apis/ItemType.api'
import customLabelApi, {
    CustomLabelUpdateRequest
} from '../apis/customLabel.api'
import Card from '../components/Card'
import CardButton from '../components/CardButton'
import Checkbox from '../components/Checkbox'
import InputText from '../components/InputText'
import Row from '../components/Row/Row'
import SearchPicker, {SearchPickerOption} from '../components/SearchPicker'
import InputFile from '../components/InputFile'
import Modal from '../components/Modal'
import {useUserPlan} from "../utils/userPlan.util";
import Tooltip from '../components/Tooltip/Tooltip'
import Icon from '../components/Icon'

interface Props {
    isCreateModalOpen: boolean
    setIsCreateModalOpen: (value: boolean) => void
    onSuccess?: () => void
    editId?: number | null
}


interface State {
    name: string
    item: string
    csvFilename: string
    apiUrl: string
    printNodePrinterId: string
    keepVersionHistory: boolean
    itemId: number | null
    items: ItemData[]
    csv: File | null
}

class CustomLabelEditForm extends Component<Props, State> {
    userPlan = useUserPlan();

    initialState: State = {
        name: '',
        item: '',
        csvFilename: '',
        apiUrl: '',
        printNodePrinterId: '',
        keepVersionHistory: false,
        itemId: null,
        csv: null,
        items: [],
    }

    constructor(props: Props) {
        super(props)
        this.state = this.initialState
    }

    closeCreateModal = () => {
        this.props.setIsCreateModalOpen(false)
        this.setState(this.initialState)
    }

    get id() {
        // return this.props.location.state.id
        return this.props.editId
    }

    get itemSearchOptions() {
        return itemService.searchOptions(this.state.items)
    }

    handleInputChange = (name: string, value: string) => {
        if (name === 'name') this.setState({name: value})
        else if (name === 'item') this.setState({item: value})
        else if (name === 'apiUrl') this.setState({apiUrl: value})
        else if (name === 'printNodePrinterId') this.setState({printNodePrinterId: value})
    }

    handleItemSearch = (name: string, term: string) => {
        const types: ItemType[] = ['production', 'receivable']
        itemApi.searchByCode(term, types, items => {
            if (!items) return
            this.setState({items})
        })
    }

    handleItemBlur = () => {
        this.setState({items: []})
    }

    handleItemSelect = (name: string, option: SearchPickerOption) => {
        const {value} = option
        const itemId = option.id as number
        const item = itemService.find(this.state.items, itemId)
        if (!item) return
        this.setState({items: [], item: value, itemId})
    }

    handleFileChange = (name: string, file: File | null, filename: string) => {
        if (name === 'csv') {
            this.setState({
                csv: file,
                csvFilename: filename
            })
        }
    }

    handleVersionHistoryToggle = () => {
        this.setState(prev => ({keepVersionHistory: !prev.keepVersionHistory}))
    }

    handleSaveConfirm = () => {
        const {editId} = this.props
        if (!editId) return
        const {name, csv, csvFilename, apiUrl, itemId, printNodePrinterId, keepVersionHistory} = this.state
        const payload: CustomLabelUpdateRequest = {}
        if (name) payload.name = name
        if (csv) payload.csv = csv
        if (csvFilename) payload.csvFilename = csvFilename
        if (apiUrl) payload.apiUrl = apiUrl
        if (itemId) payload.item = itemId
        payload.printNodePrinterId = printNodePrinterId
        // Sent as form data, so the boolean has to travel as 1/0.
        payload.keepVersionHistory = keepVersionHistory ? '1' : '0'
        customLabelApi.update(editId, payload, customLabel => {
            if (!customLabel) return
            alertService.success('Successfully updated custom label')
            this.props.onSuccess?.()
            this.closeCreateModal()
        })
    }

    handleSaveClick = () => {
        const confirm: ConfirmParams = {
            id: 1,
            question: i18n('Please review custom label details above and then confirm'),
            confirmText: i18n('Confirm'),
            cancelText: i18n('Cancel'),
            onConfirm: () => this.handleSaveConfirm()
        }
        confirmService.push(confirm)
    }

    read = () => {
        const {editId} = this.props
        if (!editId) return
        customLabelApi.read(editId, customLabel => {
            if (!customLabel) return
            const {name, item, csvFilename, apiUrl, printNodePrinterId, keepVersionHistory} = customLabel
            this.setState({
                name,
                csvFilename: csvFilename || '',
                apiUrl: apiUrl || '',
                printNodePrinterId: printNodePrinterId ? String(printNodePrinterId) : '',
                keepVersionHistory: Boolean(keepVersionHistory)
            }, () => {
                if (!item) return
                this.setState({
                    itemId: item.id,
                    item: item.code
                })
            })
        })
    }

    componentDidMount() {
        if (this.props.isCreateModalOpen && this.props.editId) {
            this.read()
        }
    }

    render() {
        if (!this.props.isCreateModalOpen) return null

        const {isCreateModalOpen} = this.props
        const {isFree, isNotFreeAndStarter, isLabelBuilderUser} = this.userPlan;

        return (
            <Modal
                title={i18n('Edit Custom Label')}
                isOpen={isCreateModalOpen}
                onClose={this.closeCreateModal}
            >
                <Card>
                    <Row column={2}>
                        <InputText
                            name="name"
                            value={this.state.name}
                            label={i18n('Name')}
                            onChange={this.handleInputChange}/>
                        {(!isLabelBuilderUser && !isFree)
                            ? (
                                <SearchPicker
                                    name="item"
                                    value={this.state.item}
                                    options={this.itemSearchOptions}
                                    label={i18n('Item code')}
                                    onSearch={this.handleItemSearch}
                                    onChange={this.handleInputChange}
                                    onSelect={this.handleItemSelect}
                                    onBlur={this.handleItemBlur}/>
                            )
                            : null}
                        <InputFile
                            className="mb-0"
                            name="csv"
                            filename={this.state.csvFilename}
                            accept=".csv"
                            label="CSV"
                            placeholder={i18n('Choose csv')}
                            onChange={this.handleFileChange}/>
                        {(!isLabelBuilderUser && isNotFreeAndStarter)
                            ? (
                                <InputText
                                    name="apiUrl"
                                    value={this.state.apiUrl}
                                    label={i18n('API URL')}
                                    onChange={this.handleInputChange}
                                />
                            )
                            : null}
                        <div className="block w-full">
                            <div className="flex items-center gap-1 mb-1">
                                <span>{i18n('PrintNode Printer ID (Optional)')}</span>
                                <Tooltip
                                    text={i18n('Optional default printer for PrintNode ZPL/PDF API printing. If an exposed API request sends printer_id, that request value is used instead. Browser and markup-only print methods do not use this field.')}
                                    maxWidth={320}
                                >
                                    <span className="flex items-center">
                                        <Icon
                                            name="question"
                                            size="small"
                                            color="gray"
                                        />
                                    </span>
                                </Tooltip>
                            </div>
                            <InputText
                                name="printNodePrinterId"
                                value={this.state.printNodePrinterId}
                                type="number"
                                onChange={this.handleInputChange}
                            />
                        </div>

                    </Row>
                    <div className="mb-4">
                        <div className="flex items-center gap-2">
                            <Checkbox
                                name="keepVersionHistory"
                                label={i18n('Keep version history')}
                                checked={this.state.keepVersionHistory}
                                onChange={this.handleVersionHistoryToggle}/>
                            <Tooltip
                                text={i18n('Each time the design is saved and activated, that version is kept so it can be reviewed or restored later. Drafts are not kept.')}
                                maxWidth={320}>
                                <span className="flex items-center">
                                    <Icon name="question" size="small" color="gray"/>
                                </span>
                            </Tooltip>
                        </div>
                    </div>
                    <CardButton
                        proceedText={i18n('Save')}
                        cancelText={i18n('Cancel')}
                        onProceedClick={this.handleSaveClick}
                        onCancelClick={this.closeCreateModal}
                    />
                </Card>
            </Modal>
        )
    }
}

export default CustomLabelEditForm
